import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader } from "@/components/common/Loader";
import { TicketStatusBadge, SlaBadge } from "@/components/common/Badges";
import { TicketThread, ReplyBox } from "@/components/common/TicketThread";
import { Field } from "@/components/common/Forms";
import { api, errMsg, useFetch } from "@/lib/api";
import { TICKET_STATUS, PRIORITY, OPEN_STATUSES, fmtDateTime } from "@/lib/format";

function Side({ t, center, staff, onPatch }) {
  const sel = (label, value, onChange, options, testid) => (
    <Field label={label}>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="bg-ivory-deep rounded-xl h-11"><SelectValue /></SelectTrigger>
        <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </Field>
  );
  const open = OPEN_STATUSES.includes(t.status);
  return (
    <aside className="space-y-5 lux-card p-6 lg:sticky lg:top-8">
      {sel("Stato", t.status, (v) => onPatch({ status: v }), Object.entries(TICKET_STATUS), "ticket-status-select")}
      {sel("Priorità", t.priority, (v) => onPatch({ priority: v }), Object.entries(PRIORITY), "ticket-priority-select")}
      {sel("Assegnato a", t.assignee_id || "none", (v) => onPatch({ assignee_id: v === "none" ? "" : v }),
        [["none", "Nessuno"], ...(staff || []).map((s) => [s.id, s.name])], "ticket-assignee-select")}
      <div className="hairline pt-5 space-y-2">
        {open && <SlaBadge label="Aperto da" since={t.created_at} testid="ticket-sla-open" />}
        {open && <div><SlaBadge label="Dall'ultima risposta" since={t.last_message_at} testid="ticket-sla-last" /></div>}
        <p className="text-xs text-warm-text">Aperto il {fmtDateTime(t.created_at)}</p>
        <p className="text-xs text-warm-text">Prima risposta: {t.first_response_at ? fmtDateTime(t.first_response_at) : "non ancora"}</p>
      </div>
      {center && (
        <div className="hairline pt-5">
          <p className="field-label">Centro</p>
          <Link to={`/admin/centri/${center.id}`} data-testid="ticket-center-link" className="font-serif text-lg underline underline-offset-4 decoration-warm">{center.center_name}</Link>
          <p className="text-xs text-warm-text mt-1">{center.city} · {center.phone}</p>
          <p className="text-xs text-warm-text break-all">{center.email}</p>
        </div>
      )}
    </aside>
  );
}

export default function AdminTicketDetail() {
  const { id } = useParams();
  const { data, loading, error, reload } = useFetch(`/admin/tickets/${id}`);
  const { data: staff } = useFetch("/admin/staff-options");
  if (loading && !data) return <Loader />;
  if (error) return <p className="text-sm text-destructive">{error}</p>;
  const { ticket: t, messages, center } = data;

  const patch = async (body) => {
    try {
      await api.patch(`/admin/tickets/${id}`, body);
      toast.success("Ticket aggiornato");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const send = async (fd) => {
    try {
      await api.post(`/admin/tickets/${id}/messages`, fd);
      await reload();
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };

  return (
    <div>
      <Link to="/admin/ticket" className="btn-link">← Coda ticket</Link>
      <div className="mt-6 mb-10 animate-fade-up">
        <div className="flex items-center gap-3"><span data-testid="admin-ticket-code" className="text-xs tracking-[0.14em] text-warm-text">{t.code}</span><TicketStatusBadge status={t.status} /></div>
        <h1 className="text-3xl sm:text-4xl font-light mt-3">{t.subject}</h1>
        <p className="text-xs text-warm-text mt-2">{t.category_name} · {t.center_name}</p>
        {t.machine && <p data-testid="ticket-machine-badge" className="mt-3 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] uppercase tracking-[0.14em] text-gold">Macchinario: {t.machine}</p>}
      </div>
      <div className="grid lg:grid-cols-[1fr_300px] gap-8 items-start">
        <div className="space-y-8 min-w-0">
          <TicketThread messages={messages} viewer="staff" />
          <ReplyBox onSend={send} allowInternal placeholder="Rispondi al centro…" />
        </div>
        <Side t={t} center={center} staff={staff} onPatch={patch} />
      </div>
    </div>
  );
}
